import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  alpha,
  createTheme,
  useTheme as useMuiTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, UploadFile as UploadFileIcon, Download as DownloadIcon, Description as DescriptionIcon, CloudDownload as CloudDownloadIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Warning as WarningIcon, SwapHoriz as SwapHorizIcon, Close as CloseIcon } from '@mui/icons-material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import SearchField from '../components/usuarios/SearchField';
import UsuarioTable from '../components/usuarios/UsuarioTable';
import UsuarioDialog from '../components/usuarios/UsuarioDialog';
import useUsuarios from '../hooks/useUsuarios';
import useServicos from '../hooks/useServicos';
import useImageUpload from '../hooks/useImageUpload';
import useSidebar from '../hooks/useSidebar';
import { getImageUrl } from '../utils/formatters';
import { createCustomTheme } from '../utils/themeHelper';

/**
 * Página de gerenciamento de usuários
 * @returns {JSX.Element}
 */
const Usuarios = () => {
  // Estados locais
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchUsuario, setSearchUsuario] = useState('');
  const [searchServico, setSearchServico] = useState('');
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [formData, setFormData] = useState({
    usuario: '',
    nome: '',
    email: '',
    senha: '',
    cargo: 'ATENDENTE',
    servicos_atendidos: {
      prioridades: {},
      atende: []
    },
    guiche: '',
    imagem_perfil: null,
  });
  
  // Hooks customizados
  const { isDarkMode } = useTheme();
  const baseTheme = useMuiTheme();
  const customTheme = createCustomTheme(baseTheme);

  const { 
    usuarios, 
    carregando: carregandoUsuarios, 
    erro: erroUsuarios,
    carregarUsuarios,
    criarUsuario,
    atualizarUsuario,
    excluirUsuario,
    atualizarGuicheUsuario,
    atualizarServicosUsuario,
    toggleAtivoUsuario,
    criarUsuariosEmMassa
  } = useUsuarios();

  const {
    servicosDisponiveis,
    carregando: carregandoServicos,
    erro: erroServicos,
    carregarServicos,
    formatarServicosAtendidosParaSalvar,
    formatarServicosAtendidosParaExibir
  } = useServicos();

  const {
    selectedImage,
    previewImage,
    cropperOpen,
    originalImagePath,
    handleImageSelect,
    handleCropComplete: handleCropCompleteHook,
    handleImageError,
    prepareImageFormData,
    setCropperOpen,
    setExistingImage,
    resetImage
  } = useImageUpload();

  // Criar um wrapper para handleCropComplete que passa o setFormData
  const handleCropComplete = (croppedImage) => {
    handleCropCompleteHook(croppedImage, setFormData);
  };

  // Abrir diálogo para criação/edição de usuário
  const handleOpenDialog = (usuario = null) => {
    if (usuario) {
      setEditingId(usuario.id);
      
      // Configurar formData
      setFormData({
        usuario: usuario.usuario,
        nome: usuario.nome,
        email: usuario.email,
        senha: '', // Não carregamos a senha para edição
        cargo: usuario.cargo,
        servicos_atendidos: formatarServicosAtendidosParaExibir(usuario.servicos_atendidos),
        guiche: usuario.guiche || '',
        imagem_perfil: null,
      });
      
      // Configurar imagem
      setExistingImage(usuario.imagem_perfil, getImageUrl);
    } else {
      setEditingId(null);
      resetImage();
      setFormData({
        usuario: '',
        nome: '',
        email: '',
        senha: '',
        cargo: 'ATENDENTE',
        servicos_atendidos: {
          prioridades: {},
          atende: []
        },
        guiche: '',
        imagem_perfil: null,
      });
    }
    setOpenDialog(true);
  };

  // Fechar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    resetImage();
    setFormData({
      usuario: '',
      nome: '',
      email: '',
      senha: '',
      cargo: 'ATENDENTE',
      servicos_atendidos: {
        prioridades: {},
        atende: []
      },
      guiche: '',
      imagem_perfil: null,
    });
    setSearchServico('');
  };

  // Abrir diálogo de usuários em massa
  const handleOpenBulkDialog = () => {
    setOpenBulkDialog(true);
  };

  // Fechar diálogo de usuários em massa
  const handleCloseBulkDialog = () => {
    setOpenBulkDialog(false);
    setSelectedFile(null);
    setUploadProgress(0);
    setIsProcessing(false);
    setUploadResults(null);
  };

  // Manipular seleção de arquivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setSelectedFile(file);
      setUploadResults(null);
    } else {
      alert('Por favor, selecione um arquivo CSV válido.');
      event.target.value = '';
    }
  };

  // Remover arquivo selecionado
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadResults(null);
    // Limpar o input de arquivo
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Trocar arquivo (trigger do input)
  const handleChangeFile = () => {
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Validar e processar CSV
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('Arquivo CSV deve conter pelo menos uma linha de dados além do cabeçalho.');
    }

    const headers = lines[0].split(',').map(h => h.trim());
    const expectedHeaders = ['usuario', 'nome', 'email', 'senha', 'cargo', 'guiche'];
    
    // Verificar se todos os cabeçalhos necessários estão presentes
    const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Cabeçalhos obrigatórios ausentes: ${missingHeaders.join(', ')}`);
    }

    const users = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      
      if (values.length !== headers.length) {
        errors.push(`Linha ${i + 1}: Número incorreto de colunas`);
        continue;
      }

      const user = {};
      headers.forEach((header, index) => {
        user[header] = values[index];
      });

      // Validações básicas
      if (!user.usuario || !user.nome || !user.email || !user.senha || !user.cargo) {
        errors.push(`Linha ${i + 1}: Campos obrigatórios em branco`);
        continue;
      }

      // Validar cargo
      if (!['ADMINISTRADOR', 'ADMIN', 'ATENDENTE', 'RECEPCAO'].includes(user.cargo.toUpperCase())) {
        errors.push(`Linha ${i + 1}: Cargo inválido (${user.cargo})`);
        continue;
      }

      // Validar email básico
      if (!/\S+@\S+\.\S+/.test(user.email)) {
        errors.push(`Linha ${i + 1}: Email inválido (${user.email})`);
        continue;
      }

      // Converter guiche para número ou null
      user.guiche = user.guiche ? parseInt(user.guiche) : null;
      
      // Mapear cargo para valores corretos do enum
      let cargoMapeado = user.cargo.toUpperCase();
      if (cargoMapeado === 'ADMINISTRADOR') {
        cargoMapeado = 'ADMIN';
      }
      user.cargo = cargoMapeado;

      users.push(user);
    }

    return { users, errors };
  };

  // Processar upload do arquivo
  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert('Por favor, selecione um arquivo primeiro.');
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);

    try {
      // Ler arquivo
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(selectedFile);
      });

      setUploadProgress(25);

      // Processar CSV
      const { users, errors } = parseCSV(fileContent);
      
      if (users.length === 0) {
        throw new Error('Nenhum usuário válido encontrado no arquivo.');
      }

      console.log('Usuários processados do CSV:', users);
      setUploadProgress(50);

      // Criar usuários em massa usando o hook
      console.log('Criando usuários em massa...');
      const result = await criarUsuariosEmMassa(users);

      setUploadProgress(100);

      if (result.success) {
        // Mostrar resultados de sucesso
        setUploadResults({
          success: true,
          created: result.created || 0,
          errors: [...errors, ...(result.errors || [])],
          total: users.length
        });
      } else {
        // Mostrar erro
        setUploadResults({
          success: false,
          error: result.error || 'Erro ao processar usuários',
          created: 0,
          errors: [...errors],
          total: users.length
        });
      }

    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadResults({
        success: false,
        error: error.message,
        created: 0,
        errors: [],
        total: 0
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Função para fazer download do template CSV
  const handleDownloadTemplate = () => {
    // Definir o cabeçalho e dados de exemplo do CSV
    const csvHeader = [
      'usuario',
      'nome', 
      'email',
      'senha',
      'cargo',
      'guiche'
    ];

    const csvExampleData = [
      [
        'joao.silva',
        'João Silva',
        'joao.silva@empresa.com',
        'senha123',
        'ATENDENTE',
        '1'
      ],
      [
        'maria.santos',
        'Maria Santos', 
        'maria.santos@empresa.com',
        'senha456',
        'ADMIN',
        '2'
      ]
    ];

    // Criar o conteúdo CSV
    const csvContent = [
      csvHeader.join(','),
      ...csvExampleData.map(row => row.join(','))
    ].join('\n');

    // Criar e fazer download do arquivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_usuarios_massa.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Manipular envio do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Validate required fields
      const requiredFields = ['usuario', 'nome', 'email', 'cargo'];
      if (!editingId) {
        requiredFields.push('senha');
      }
      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        alert(`Por favor, preencha os campos obrigatórios: ${missingFields.join(', ')}`);
        return;
      }

      // Adiciona os campos de texto ao FormData
      Object.keys(formData).forEach(key => {
        // Pula o campo senha se estiver editando e o campo estiver vazio
        if (key === 'senha' && editingId && !formData.senha) {
          return;
        }
        
        if (key === 'imagem_perfil') {
          prepareImageFormData(formData, formDataToSend);
        } else if (key === 'guiche') {
          // Converte para número ou null se vazio
          const guicheValue = formData.guiche ? parseInt(formData.guiche) : null;
          if (guicheValue !== null) {
            formDataToSend.append('guiche', guicheValue);
          }
        } else if (key === 'servicos_atendidos') {
          // Preparar serviços atendidos para salvar
          const servicosFormatados = formatarServicosAtendidosParaSalvar(formData.servicos_atendidos);
          formDataToSend.append('servicos_atendidos', JSON.stringify(servicosFormatados));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      let result;
      if (editingId) {
        result = await atualizarUsuario(editingId, formDataToSend);
      } else {
        result = await criarUsuario(formDataToSend);
      }
      
      if (result.success) {
        handleCloseDialog();
      } else {
        alert(result.error || 'Erro ao salvar usuário. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      alert('Erro ao salvar usuário. Por favor, tente novamente.');
    }
  };

  // Manipular exclusão de usuário
  const handleDelete = async (id) => {
    try {
      const result = await toggleAtivoUsuario(id);
      if (!result.success) {
        alert(result.error || 'Erro ao atualizar status do usuário. Por favor, tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao atualizar status do usuário:', error);
      alert('Erro ao atualizar status do usuário. Por favor, tente novamente.');
    }
  };

  // Manipular alteração da pesquisa de usuários
  const handleUserSearchChange = (e) => {
    setSearchUsuario(e.target.value);
  };

  // Limpar pesquisa de usuários
  const handleClearUserSearch = () => {
    setSearchUsuario('');
  };

  // Manipular toggle de serviço
  const handleToggleServico = (servicoId) => {
    setFormData((prevData) => {
      const atende = [...prevData.servicos_atendidos.atende];
      
      if (atende.includes(servicoId)) {
        // Remove o serviço da lista
        const index = atende.indexOf(servicoId);
        atende.splice(index, 1);
      } else {
        // Adiciona o serviço à lista
        atende.push(servicoId);
      }

      return {
        ...prevData,
        servicos_atendidos: {
          ...prevData.servicos_atendidos,
          atende
        }
      };
    });
  };

  // Manipular alteração de prioridade
  const handlePrioridadeChange = (servicoId, nivel) => {
    setFormData((prevData) => {
      return {
        ...prevData,
        servicos_atendidos: {
          ...prevData.servicos_atendidos,
          prioridades: {
            ...prevData.servicos_atendidos.prioridades,
            [servicoId]: nivel
          }
        }
      };
    });
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: alpha(customTheme.palette.primary.main, 0.03) }}>
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        customTheme={customTheme} 
      />
      
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header 
          sidebarOpen={sidebarOpen} 
                      handleSidebarToggle={toggleSidebar}
          customTheme={customTheme}
        />
        
        <Container maxWidth="100%" sx={{ mt: 10, mb: 4 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3,
              borderRadius: 4,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
              background: theme => theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
                : 'background.paper',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography 
                variant="h5" 
                component="h1"
                sx={{
                  fontWeight: 600,
                  background: theme => theme.palette.mode === 'dark'
                    ? `linear-gradient(90deg, ${customTheme.palette.primary.light} 0%, ${customTheme.palette.primary.main} 100%)`
                    : `linear-gradient(90deg, ${customTheme.palette.primary.main} 0%, ${customTheme.palette.primary.dark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Gerenciamento de Usuários
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={handleOpenBulkDialog}
                  sx={{
                    borderRadius: '12px',
                    py: 1.5,
                    px: 3,
                    border: `2px solid ${customTheme.palette.primary.main}`,
                    color: customTheme.palette.primary.main,
                    transition: 'all 0.3s ease',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      border: `2px solid ${customTheme.palette.primary.dark}`,
                      color: customTheme.palette.primary.dark,
                      backgroundColor: alpha(customTheme.palette.primary.main, 0.1),
                      boxShadow: `0 8px 20px ${alpha(customTheme.palette.primary.main, 0.3)}`,
                    }
                  }}
                >
                  Usuários em Massa
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{
                    borderRadius: '12px',
                    py: 1.5,
                    px: 3,
                    background: `linear-gradient(45deg, ${customTheme.palette.primary.main}, ${customTheme.palette.primary.dark})`,
                    boxShadow: `0 8px 20px ${alpha(customTheme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    border: `1px solid ${alpha(customTheme.palette.primary.main, 0.5)}`,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 12px 25px ${alpha(customTheme.palette.primary.main, 0.5)}`,
                      background: `linear-gradient(45deg, ${customTheme.palette.primary.dark}, ${customTheme.palette.primary.main})`,
                    }
                  }}
                >
                  Novo Usuário
                </Button>
              </Box>
            </Box>

            {/* Campo de pesquisa de usuários */}
            <Box sx={{ mb: 3 }}>
              <SearchField 
                value={searchUsuario}
                onChange={handleUserSearchChange}
                onClear={handleClearUserSearch}
                placeholder="Buscar usuário por nome, e-mail, usuário ou cargo..."
                customTheme={customTheme}
              />
            </Box>

            {/* Tabela de usuários */}
            <UsuarioTable 
              usuarios={usuarios}
              searchTerm={searchUsuario}
              onSearchChange={handleUserSearchChange}
              onClearSearch={handleClearUserSearch}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
              getImageUrl={getImageUrl}
              handleImageError={handleImageError}
              customTheme={customTheme}
              atualizarGuicheUsuario={atualizarGuicheUsuario}
              atualizarServicosUsuario={atualizarServicosUsuario}
            />
          </Paper>
        </Container>
      </Box>

      {/* Diálogo de criação/edição de usuário */}
      <UsuarioDialog 
        open={openDialog}
        onClose={handleCloseDialog}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        editingId={editingId}
        previewImage={previewImage}
        cropperOpen={cropperOpen}
        selectedImage={selectedImage}
        handleImageSelect={handleImageSelect}
        handleCropComplete={handleCropComplete}
        handleImageError={handleImageError}
        customTheme={customTheme}
        servicosDisponiveis={servicosDisponiveis}
        searchServico={searchServico}
        setSearchServico={setSearchServico}
        handleToggleServico={handleToggleServico}
        handlePrioridadeChange={handlePrioridadeChange}
        carregandoServicos={carregandoServicos}
        erroServicos={erroServicos}
        carregarServicos={carregarServicos}
        setCropperOpen={setCropperOpen}
      />

      {/* Diálogo de usuários em massa */}
      <Dialog 
        open={openBulkDialog} 
        onClose={handleCloseBulkDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: theme => theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
              : 'background.paper',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
            boxShadow: `0 20px 40px ${alpha(customTheme.palette.primary.main, 0.2)}`,
          }
        }}
      >
        <DialogTitle 
          sx={{
            background: `linear-gradient(90deg, ${customTheme.palette.primary.main} 0%, ${customTheme.palette.primary.dark} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 600,
            fontSize: '2rem',
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <UploadFileIcon 
            sx={{ 
              fontSize: '2rem',
              color: customTheme.palette.primary.main 
            }} 
          />
          Importar Usuários em Massa
        </DialogTitle>
        
        <DialogContent sx={{ pt: 0, pb: 3 }}>
          {/* Cards de instruções */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
            
            {/* Card 1: Download Template */}
            <Card 
              elevation={0}
              sx={{
                background: theme => theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${alpha(customTheme.palette.primary.main, 0.1)} 0%, ${alpha(customTheme.palette.primary.dark, 0.05)} 100%)`
                  : `linear-gradient(135deg, ${alpha(customTheme.palette.primary.main, 0.05)} 0%, ${alpha(customTheme.palette.primary.light, 0.1)} 100%)`,
                border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CloudDownloadIcon 
                    sx={{ 
                      color: customTheme.palette.primary.main, 
                      fontSize: '1.8rem',
                      mr: 1.5 
                    }} 
                  />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    1. Baixar Template
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, flexGrow: 1 }}>
                  Baixe o arquivo modelo em formato CSV contendo todos os campos obrigatórios e exemplos de preenchimento correto. 
                  Este template garante que você forneça as informações no formato adequado para importação.
                </Typography>
                
                <Box 
                  sx={{ 
                    mb: 2, 
                    p: 1.5, 
                    backgroundColor: alpha(customTheme.palette.warning.main, 0.1),
                    border: `1px solid ${alpha(customTheme.palette.warning.main, 0.3)}`,
                    borderRadius: 2 
                  }}
                >
                  <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                    ⚠️ Importante: Não apague a primeira linha (cabeçalho) do arquivo CSV!
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  fullWidth
                  sx={{
                    borderRadius: '10px',
                    py: 1.5,
                    background: `linear-gradient(45deg, ${customTheme.palette.primary.main}, ${customTheme.palette.primary.dark})`,
                    boxShadow: `0 6px 16px ${alpha(customTheme.palette.primary.main, 0.3)}`,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 20px ${alpha(customTheme.palette.primary.main, 0.4)}`,
                    }
                  }}
                >
                  Baixar Template CSV
                </Button>
              </CardContent>
            </Card>

            {/* Card 2: Upload Arquivo */}
            <Card 
              elevation={0}
              sx={{
                background: theme => theme.palette.mode === 'dark'
                  ? `linear-gradient(135deg, ${alpha(customTheme.palette.secondary.main, 0.1)} 0%, ${alpha(customTheme.palette.secondary.dark, 0.05)} 100%)`
                  : `linear-gradient(135deg, ${alpha(customTheme.palette.secondary.main, 0.05)} 0%, ${alpha(customTheme.palette.secondary.light, 0.1)} 100%)`,
                border: `1px solid ${alpha(customTheme.palette.secondary.main, 0.2)}`,
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <UploadFileIcon 
                    sx={{ 
                      color: customTheme.palette.secondary.main, 
                      fontSize: '1.8rem',
                      mr: 1.5 
                    }} 
                  />
                  <Typography variant="h6" fontWeight="bold" color="secondary">
                    2. Enviar Arquivo
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, flexGrow: 1 }}>
                  Após preencher o template com os dados dos usuários, selecione o arquivo CSV preenchido e clique em "Importar Usuários" 
                  para adicionar todos os usuários de uma só vez ao sistema.
                </Typography>
                
                {/* Input de arquivo */}
                <Box sx={{ mb: 2 }}>
                  <input
                    accept=".csv"
                    style={{ display: 'none' }}
                    id="csv-file-input"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  
                  {!selectedFile ? (
                    // Botão para selecionar arquivo
                    <label htmlFor="csv-file-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadFileIcon />}
                        fullWidth
                        sx={{
                          borderRadius: '10px',
                          py: 1.5,
                          border: `2px dashed ${customTheme.palette.secondary.main}`,
                          color: customTheme.palette.secondary.main,
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: '0.95rem',
                          '&:hover': {
                            backgroundColor: alpha(customTheme.palette.secondary.main, 0.1),
                            border: `2px dashed ${customTheme.palette.secondary.dark}`,
                          }
                        }}
                      >
                        Selecionar Arquivo CSV
                      </Button>
                    </label>
                  ) : (
                    // Arquivo selecionado com botões de ação
                    <Box 
                      sx={{ 
                        position: 'relative',
                        p: 3, 
                        backgroundColor: alpha(customTheme.palette.secondary.main, 0.1), 
                        borderRadius: 3,
                        border: `1px solid ${alpha(customTheme.palette.secondary.main, 0.3)}`,
                      }}
                    >
                      {/* Botões de ação no canto superior direito */}
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8, 
                          display: 'flex', 
                          gap: 0.5 
                        }}
                      >
                        {/* Botão trocar arquivo */}
                        <Tooltip title="Trocar arquivo" placement="top" arrow>
                          <Button
                            size="small"
                            onClick={handleChangeFile}
                            sx={{
                              minWidth: 'auto',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              padding: 0,
                              backgroundColor: customTheme.palette.primary.main,
                              color: 'white',
                              '&:hover': {
                                backgroundColor: customTheme.palette.primary.dark,
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <SwapHorizIcon sx={{ fontSize: '1rem' }} />
                          </Button>
                        </Tooltip>
                        
                        {/* Botão remover arquivo */}
                        <Tooltip title="Remover arquivo" placement="top" arrow>
                          <Button
                            size="small"
                            onClick={handleRemoveFile}
                            sx={{
                              minWidth: 'auto',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              padding: 0,
                              backgroundColor: customTheme.palette.error.main,
                              color: 'white',
                              '&:hover': {
                                backgroundColor: customTheme.palette.error.dark,
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <CloseIcon sx={{ fontSize: '1rem' }} />
                          </Button>
                        </Tooltip>
                      </Box>
                      
                      {/* Informações do arquivo */}
                      <Box sx={{ pr: 10 }}>
                        <Typography variant="body2" color="secondary.main" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon sx={{ fontSize: '1.2rem', mr: 1 }} />
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Tamanho: {(selectedFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Barra de progresso */}
                {isProcessing && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Processando arquivo...
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress} 
                      sx={{ 
                        borderRadius: 2,
                        height: 6,
                        backgroundColor: alpha(customTheme.palette.secondary.main, 0.2),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: customTheme.palette.secondary.main,
                        }
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {uploadProgress}% concluído
                    </Typography>
                  </Box>
                )}
                
                <Button
                  variant="contained"
                  startIcon={isProcessing ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  onClick={handleFileUpload}
                  disabled={!selectedFile || isProcessing}
                  fullWidth
                  sx={{
                    borderRadius: '10px',
                    py: 1.5,
                    background: selectedFile && !isProcessing 
                      ? `linear-gradient(45deg, ${customTheme.palette.secondary.main}, ${customTheme.palette.secondary.dark})`
                      : alpha(customTheme.palette.secondary.main, 0.3),
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      background: selectedFile && !isProcessing 
                        ? `linear-gradient(45deg, ${customTheme.palette.secondary.dark}, ${customTheme.palette.secondary.main})`
                        : alpha(customTheme.palette.secondary.main, 0.3),
                      transform: selectedFile && !isProcessing ? 'translateY(-2px)' : 'none',
                    },
                    '&:disabled': {
                      background: alpha(customTheme.palette.secondary.main, 0.3),
                      color: alpha(customTheme.palette.common.white, 0.6),
                    }
                  }}
                >
                  {isProcessing ? 'Processando...' : 'Importar Usuários'}
                </Button>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Resultados do Upload */}
          {uploadResults && (
            <Card 
              elevation={0}
              sx={{
                background: uploadResults.success 
                  ? `linear-gradient(135deg, ${alpha(customTheme.palette.success.main, 0.1)} 0%, ${alpha(customTheme.palette.success.light, 0.05)} 100%)`
                  : `linear-gradient(135deg, ${alpha(customTheme.palette.error.main, 0.1)} 0%, ${alpha(customTheme.palette.error.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(uploadResults.success ? customTheme.palette.success.main : customTheme.palette.error.main, 0.3)}`,
                borderRadius: 3,
                mb: 3,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {uploadResults.success ? (
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.8rem', mr: 1.5 }} />
                  ) : (
                    <ErrorIcon sx={{ color: 'error.main', fontSize: '1.8rem', mr: 1.5 }} />
                  )}
                  <Typography variant="h6" fontWeight="bold" color={uploadResults.success ? 'success.main' : 'error.main'}>
                    {uploadResults.success ? 'Importação Concluída!' : 'Erro na Importação'}
                  </Typography>
                </Box>
                
                {uploadResults.success ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>{uploadResults.created}</strong> usuários foram criados com sucesso de <strong>{uploadResults.total}</strong> registros processados.
                    </Typography>
                    
                    {uploadResults.errors.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                          <WarningIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                          Avisos encontrados:
                        </Typography>
                        <Box sx={{ maxHeight: 150, overflowY: 'auto', backgroundColor: alpha(customTheme.palette.warning.main, 0.1), borderRadius: 1, p: 1 }}>
                          {uploadResults.errors.map((error, index) => (
                            <Typography key={index} variant="caption" color="warning.dark" sx={{ display: 'block', mb: 0.5 }}>
                              • {error}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="error.main">
                    {uploadResults.error}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Instruções detalhadas */}
          <Card 
            elevation={0}
            sx={{
              background: 'transparent',
              border: `1px solid ${alpha(customTheme.palette.grey[500], 0.2)}`,
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon 
                  sx={{ 
                    color: customTheme.palette.info.main, 
                    fontSize: '1.5rem',
                    mr: 1.5 
                  }} 
                />
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  Instruções de Preenchimento
                </Typography>
              </Box>
              
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: customTheme.palette.primary.main 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                                             <Typography variant="body2">
                         <strong>usuario:</strong> Nome de usuário único (sem espaços, letras e números apenas)
                       </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: customTheme.palette.primary.main 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2">
                        <strong>nome:</strong> Nome completo do usuário
                      </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: customTheme.palette.primary.main 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2">
                        <strong>email:</strong> Endereço de e-mail válido
                      </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: customTheme.palette.primary.main 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                                             <Typography variant="body2">
                         <strong>senha:</strong> Senha inicial do usuário (pode ser alterada após o login)
                       </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: customTheme.palette.primary.main 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body2">
                        <strong>cargo:</strong> ADMIN, ATENDENTE ou RECEPCAO
                      </Typography>
                    }
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: customTheme.palette.primary.main 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                                             <Typography variant="body2">
                         <strong>guiche:</strong> Número do guichê de atendimento (deixe vazio se não aplicável)
                       </Typography>
                    }
                  />
                </ListItem>
              </List>
              
              <Box 
                sx={{ 
                  mt: 3, 
                  p: 2, 
                  backgroundColor: alpha(customTheme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(customTheme.palette.info.main, 0.3)}`,
                  borderRadius: 2 
                }}
              >
                <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                  📋 Nota Importante:
                </Typography>
                                 <Typography variant="body2" color="text.secondary">
                   Os <strong>serviços atendidos</strong> não devem ser incluídos no arquivo CSV. Eles serão configurados 
                   individualmente após a importação, através da tela de edição de cada usuário. 
                   Isso garante maior precisão e evita erros de digitação nos nomes dos serviços.
                 </Typography>
              </Box>
            </CardContent>
          </Card>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseBulkDialog}
            size="large"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Usuarios; 